/** SASE Analytics (SASE-14): the shapes of `/sase/logs/…`
 *  (backend `app/schemas/sase_analytics.py`). Every answer is the caller's own
 *  organisation's only; the tenant comes from the session, never from here. */

/** What produced a line: a web request, a DNS lookup, a firewall flow or a
 *  threat alert. */
export type LogKind = "web" | "dns" | "fw" | "threat";
export const LOG_KINDS: readonly LogKind[] = ["web", "dns", "fw", "threat"];

/** Every decision the log can carry (web, DNS, firewall and threat). */
export type LogAction =
  | "allow"
  | "inspect"
  | "bypass"
  | "block"
  | "refuse"
  | "canary"
  | "safe_search"
  | "error"
  | "warn"
  | "isolate"
  | "alert"
  | "drop"
  | "log"
  | "read_only";
export const LOG_ACTIONS: readonly LogAction[] = [
  "allow",
  "inspect",
  "bypass",
  "block",
  "refuse",
  "canary",
  "safe_search",
  "error",
  "warn",
  "isolate",
  "alert",
  "drop",
  "log",
  "read_only",
];

/** The fields a top-N may group by. */
export type TopField =
  | "user"
  | "device"
  | "app"
  | "host"
  | "category"
  | "action"
  | "rule_id"
  | "kind"
  | "pop"
  | "node"
  | "qname"
  | "signature"
  | "threat_category"
  | "web_category"
  | "ai_app"
  | "ai_category"
  | "casb_app"
  | "casb_activity"
  | "mcp_server";
export type TopMetric = "count" | "bytes";

/** A threat's severity as the API filters it: high = 1, medium = 2, low = 3
 *  or more (the IPS number the record stores). */
export type Severity = "high" | "medium" | "low";
export const SEVERITIES: readonly Severity[] = ["high", "medium", "low"];

/** The Activity search's filters. Every value is matched exactly, except
 *  `q` (words in the host, path, name, user or reason). */
export interface ActivityFilters {
  kinds?: LogKind[];
  user?: string;
  device?: string;
  /** App, host or SNI. */
  app?: string;
  category?: string;
  action?: LogAction;
  rule_id?: string;
  /** Location id or name. */
  pop?: string;
  txn_id?: string;
  /** Threats of this severity. */
  severity?: Severity;
  /** Threats of this category (exact). */
  threat_category?: string;
  /** One of the site's web categories (B14b). */
  web_category?: string;
  /** The AI app (B14b). */
  ai_app?: string;
  /** The AI app's category (B14b). */
  ai_category?: string;
  /** The cloud app of an app activity (B14c). */
  casb_app?: string;
  /** The app activity: upload, share, …, unknown (B14c). */
  casb_activity?: string;
  /** The MCP server (host) of an MCP request (B20, N34). */
  mcp_server?: string;
  /** These records only, by event_id (A18: the evidence of an unusual-activity finding). */
  event_ids?: string[];
  q?: string;
}

/** One file the antivirus check scanned (never its content or name). */
export interface ActivityAvFile {
  type?: string | null;
  size?: number | null;
  sha256?: string | null;
  md5?: string | null;
  /** clean, infected, blocked or incomplete. */
  verdict?: string | null;
  signature?: string | null;
  /** How the scan went: ok, error, busy, partial or off. */
  clamd?: string | null;
  encrypted?: string | null;
  archive_depth?: number | null;
  rule?: string | null;
  /** held (checked before release) or streamed. */
  mode?: string | null;
}

/** The antivirus detail of a line: the file seen each way and a URL or feed
 *  verdict (B14). */
export interface ActivityAv {
  upload?: ActivityAvFile | null;
  download?: ActivityAvFile | null;
  url?: string | null;
}

/** One data-protection rule hit: never the matched text. */
export interface ActivityAiFinding {
  rule: string;
  identifier: string;
  count: number;
}

/** SASE-24 (P37): the deciding fingerprint rule's best document and the numbers. */
export interface ActivityFingerprint {
  rule?: string | null;
  set?: string | null;
  doc?: string | null;
  score?: number | null;
  shared?: number | null;
  distance?: number | null;
  unavailable?: string | null;
  budget_exceeded?: string | null;
}

/** The AI app detail of a line (B14). */
export interface ActivityAi {
  app?: string | null;
  category?: string | null;
  /** prompt, upload, upload_intent or stream. */
  kind?: string | null;
  /** allow, block, read_only or corporate_only. */
  control?: string | null;
  findings?: ActivityAiFinding[] | null;
  /** SASE-24 (P37): the document-fingerprint facts (``dlp.fp.*``): ids and numbers. An
   *  ``fp/<set>/<doc>`` finding's ``count`` is its likeness (0-100), never added up. */
  fp?: ActivityFingerprint | null;
  /** Exact data lists that could not be checked: "<rule>:<why>,…" (B14c). */
  edm_unavailable?: string | null;
  edm_budget_exceeded?: string | null;
  /** Long keys or tokens in the answer held back whole (B14c). */
  response_token_bound?: number | null;
  /** Identifiers whose start had already gone out before the rest was masked (B14c). */
  response_hold_back_straddled?: number | null;
  /** B20 (N34): an MCP request's facts; `kind` is then "mcp". */
  mcp?: ActivityMcp | null;
  /** B20 (N34): what the injection check reported (never the text it read). */
  injection?: ActivityInjection | null;
  /** B20 (N34): the conversation window. "app" (the app named the conversation) or "derived". */
  conversation?: string | null;
  /** Groups one conversation's lines (not the app's own id). */
  conversation_key?: string | null;
  /** Earlier prompts checked together with this one. */
  window_turns?: number | null;
  /** Values split across prompts: "<rule>:<identifier>=<n>,…". */
  window_findings?: string | null;
  window_capped?: boolean | null;
}

/** An MCP request (B20, N34). `tool`, `client_info` and `server_info` are chosen
 *  by clients and servers: shown as plain text only. */
export interface ActivityMcp {
  server?: string | null;
  method?: string | null;
  tool?: string | null;
  client_info?: string | null;
  server_info?: string | null;
  protocol_version?: string | null;
  /** streamable_http, http_sse or http. */
  transport?: string | null;
  approved?: boolean | null;
  /** "unapproved" when the organisation alerts on unapproved servers. */
  alert?: string | null;
  detected_by?: string | null;
  session?: boolean | null;
  batch?: number | null;
  resource_scheme?: string | null;
  header_mismatch?: string | null;
  truncated?: boolean | null;
  tools?: number | null;
  messages?: number | null;
  response_oversize?: number | null;
}

export interface ActivityInjection {
  score?: number | null;
  /** The detector's clue names (override, hidden, role, exfil, …). */
  signals?: string[] | null;
  /** response, tool_result, tool_description, resource, prompt, mcp_… */
  source?: string | null;
  /** alert or block. */
  action?: string | null;
  scanned?: number | null;
  capped?: boolean | null;
  uninspected?: string | null;
}

/** The cloud app activity of a line (B14c, N33): each may be "unknown". */
export interface ActivityCasb {
  app?: string | null;
  activity?: string | null;
  instance?: string | null;
}

/** B18 (SASE-52): how a web line's encryption keys were agreed, per side
 *  (quantum-safe = a hybrid quantum-safe key exchange); a side that did not
 *  agree keys here is absent. */
export interface ActivityKeyExchange {
  users_group?: string | null;
  users_quantum_safe?: boolean | null;
  websites_group?: string | null;
  websites_quantum_safe?: boolean | null;
  proxy_group?: string | null;
  proxy_quantum_safe?: boolean | null;
  offered_quantum_safe?: boolean | null;
  end_to_end_group?: string | null;
  end_to_end_quantum_safe?: boolean | null;
}

/** One line: one transaction, lookup, flow or alert. */
/** B28 (N43 §3.6): what DNS threat detection found in one query. */
export interface ActivityDnsDetection {
  dga?: { score?: number | null; hit?: boolean | null } | null;
  tunnel?: {
    hit?: boolean | null;
    rule?: string | null;
    domain?: string | null;
    queries?: number | null;
    unique?: number | null;
    txt_null?: number | null;
    encoded?: number | null;
    bytes?: number | null;
    sub_len?: number | null;
    label_len?: number | null;
    entropy?: number | null;
  } | null;
  newly_seen?: boolean | null;
}

export interface ActivityEntry {
  event_id: string;
  timestamp: string;
  kind: LogKind;
  txn_id?: string | null;
  action?: string | null;
  rule_id?: string | null;
  /** The rule as a person reads it (C9-F2): its name in your policy,
   *  "Deleted rule (<first 8 of its id>)", or a built-in rule's label. */
  rule_name?: string | null;
  reason?: string | null;
  user?: string | null;
  device?: string | null;
  app?: string | null;
  category?: string | null;
  client_addr?: string | null;
  client_port?: number | null;
  pop_id?: string | null;
  pop_name?: string | null;
  node_id?: string | null;
  node_name?: string | null;
  listener?: string | null;
  duration_ms?: number | null;
  sni?: string | null;
  host?: string | null;
  method?: string | null;
  path?: string | null;
  status?: number | null;
  bytes_in?: number | null;
  bytes_out?: number | null;
  stages?: string[] | null;
  qname?: string | null;
  qtype?: string | null;
  rcode?: string | null;
  proto?: string | null;
  answers?: number | null;
  cache?: string | null;
  src_addr?: string | null;
  dst_addr?: string | null;
  dst_port?: number | null;
  signature?: string | null;
  severity?: number | null;
  threat_category?: string | null;
  /** The site's web categories, when the category check ran. */
  categories?: string[] | null;
  av?: ActivityAv | null;
  ai?: ActivityAi | null;
  casb?: ActivityCasb | null;
  /** Why a read-only rule blocked: the write activity, or "unknown". */
  read_only_write?: string | null;
  key_exchange?: ActivityKeyExchange | null;
  /** B28 (N43): DNS threat detection findings of a DNS line. */
  dns?: ActivityDnsDetection | null;
}

export interface ActivitySearchResponse {
  items: ActivityEntry[];
  total_returned: number;
  truncated: boolean;
  next_cursor: string | null;
  since: string;
  until?: string | null;
}

export interface Bucket {
  key: string;
  count: number;
  bytes?: number | null;
  /** What the key reads as: a location's name when the key is its id. */
  name?: string | null;
}

export interface TopResponse {
  field: TopField;
  metric: TopMetric;
  buckets: Bucket[];
  since: string;
  until?: string | null;
}

export interface Breakdown {
  total: number;
  by_kind: Bucket[];
  by_action: Bucket[];
  /** Top hosts (user view) or top users (app view). */
  top: Bucket[];
  bytes?: number | null;
}

export interface UserView {
  user: string;
  summary: Breakdown;
  timeline: ActivitySearchResponse;
}

/** One private access event (the access log's line), as the user view reads
 *  it: a sign-in, a session, app access, or a decision with the device checks
 *  behind it. */
export interface AccessEvent {
  id?: string | null;
  timestamp: string;
  feed: "engine" | "product";
  event: string;
  outcome?: string | null;
  decision?: "allow" | "block" | null;
  reason?: string | null;
  username?: string | null;
  app_name?: string | null;
  policy_name?: string | null;
  posture?: string | null;
  connector_name?: string | null;
  client_platform?: string | null;
  client_version?: string | null;
  source_address?: string | null;
  country?: string | null;
  duration_ms?: number | null;
  message: string;
}

/** `ok` (the items, possibly none), `not_set_up` (no private access in this
 *  organisation), `unavailable` (the access log cannot be read now). */
export type AccessEventsState = "ok" | "not_set_up" | "unavailable";

export interface UserAccessEvents {
  user: string;
  state: AccessEventsState;
  detail?: string | null;
  items: AccessEvent[];
  truncated: boolean;
  since: string;
  until?: string | null;
}

export interface AppView {
  app: string;
  summary: Breakdown;
  timeline: ActivitySearchResponse;
}

/** The time window of a read: `since`/`until` as ISO strings (either may be
 *  absent; the API defaults to the last 24 hours). */
export interface Window {
  since?: string;
  until?: string;
}
